<script setup lang="ts">
type Variant = "info" | "warning" | "danger" | "action";

const { showIcon = true } = defineProps<{
  variant: Variant;
  title?: string;
  showIcon?: boolean;
}>();

const iconMap: Record<Variant, keyof typeof iconPackMapper> = {
  info: "circleInfoOutlined",
  warning: "circleWarningOutlined",
  danger: "circleCloseOutlined",
  action: "circleInfoOutlined",
};

const platformStore = usePlatformStore();
const { getStyleFromTheme } = useGetStyleFromTheme();
const platformStyles = computed(() =>
  getStyleFromTheme(platformStore.getPlatformConfiguration.styling),
);
</script>

<template>
  <div
    :style="platformStyles"
    class="px-5 py-4 flex flex-row gap-2 items-start border"
    :class="[
      {
        'border-platform-danger text-platform-danger background-danger':
          variant === 'danger',
        'border-platform-bg-3 text-platform-text-1 background-info':
          variant === 'info',
        'border-platform-warning text-platform-warning background-warning':
          variant === 'warning',
        'border-platform-neutral text-platform-neutral background-neutral':
          variant === 'action',
      },
    ]"
  >
    <BaseIcon
      v-if="showIcon"
      :name="iconMap[variant]"
      :size="24"
      class="pr-2"
      :class="title && 'py-3'"
    />
    <div class="flex flex-col gap-1">
      <h2
        v-if="title"
        class="uppercase font-bold"
      >
        {{ title }}
      </h2>
      <div class="text-sm">
        <slot />
      </div>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.background-danger {
  background-color: rgba(var(--danger-rgb), 0.05);
}

.background-info {
  background-color: rgba(var(--background-3-rgb), 0.05);
}

.background-warning {
  background-color: rgba(var(--warning-rgb), 0.05);
}

.background-neutral {
  background-color: rgba(var(--neutral-rgb), 0.05);
}
</style>
